import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  TableBody,
} from '@mui/material';
import qs from 'qs';

import AnimationPage from '../../../pages/AnimationPage';
import { FormInputText } from '../../../form-components/FormInputText';
import CommonPagination from '../../../form-components/CommonPagination';
import CommonSearchSubmitButton from '../../../form-components/CommonSearchSubmitButton';
import {
  approvePassById,
  approveTicketById,
  getPassTransferReceivers,
  getTicketTransferReceivers,
  cancelPassTransfer,
  cancelProductTransfer,
  cancelLockerTransfer,
  cancelTicketTransfer,
  getAllTransferPasses,
  getAllTransferProducts,
  getAllTransferLockers,
  getAllTransferTickets,
  getAllTransfersMerged,
} from '../../../api/transfer';

import useUserAuth from '../../../utils/useUserAuth';
import {
  ApproveButton,
  CancelButton,
  BodyCell,
  BodyRow,
  Card,
  HeaderCell,
  NameCellInner,
  NameText,
  PendingDot,
  SearchButtonWrapper,
  SearchGroup,
  SearchLabel,
  SearchRow,
  StyledTable,
  StyledTableContainer,
  StyledTableHead,
} from './TransferStyled';

const STATUS_PENDING = 'APPROVAL_PENDING';
const STATUS_APPROVED = 'APPROVED';
const STATUS_CANCELED = 'CANCELED';

// 양도 종류별 설정 (Ticket / Pass 차이를 한 곳에 모아둔다)
const TRANSFER_CONFIG = {
  TICKET: {
    queryKey: 'ticketTransfers',
    fetchList: getAllTransferTickets,
    approve: approveTicketById,
    fetchReceivers: getTicketTransferReceivers,
    cancel: cancelTicketTransfer,
    nameField: 'ticketName',
    idField: 'ticketTransferId',
    headerLabelKey: 'Transferable ticket',
  },
  PASS: {
    queryKey: 'passTransfers',
    fetchList: getAllTransferPasses,
    approve: approvePassById,
    fetchReceivers: getPassTransferReceivers,
    cancel: cancelPassTransfer,
    nameField: 'passName',
    idField: 'passTransferId',
    headerLabelKey: 'Transferable pass',
  },
  // 기간 상품은 CRM 직접양도만 있어 승인 대기(APPROVAL_PENDING) 행이 생기지 않는다 — approve/fetchReceivers 없음.
  PRODUCT: {
    queryKey: 'productTransfers',
    fetchList: getAllTransferProducts,
    cancel: cancelProductTransfer,
    nameField: 'productName',
    idField: 'productTransferId',
    headerLabelKey: '양도할 기간 상품',
  },
  // 락커(#166)도 CRM 직접양도만 — approve/fetchReceivers 없음.
  LOCKER: {
    queryKey: 'lockerTransfers',
    fetchList: getAllTransferLockers,
    cancel: cancelLockerTransfer,
    nameField: 'lockerName',
    idField: 'lockerTransferId',
    headerLabelKey: '양도할 라커',
  },
  // 전체 탭 — 네 종류 기록을 합친 목록. 승인·롤백은 행의 type 으로 원래 종류 설정을 골라 호출한다.
  ALL: {
    queryKey: 'allTransfers',
    fetchList: getAllTransfersMerged,
    nameField: 'productName',
    idField: 'transferId',
    headerLabelKey: '양도 상품',
  },
};

const TYPE_LABEL = { TICKET: '수강권', PASS: '이용권', PRODUCT: '기간 상품', LOCKER: '라커' };

const TransferTable = ({ type }) => {
  const config = TRANSFER_CONFIG[type];
  // 전체 탭이면 행마다 원래 종류(TICKET/PASS/PRODUCT)의 설정으로 승인·롤백 API 를 고른다.
  const configOf = row => (type === 'ALL' ? TRANSFER_CONFIG[row?.type] : config);
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canApprove = useUserAuth('COMMUNITY_TRANSFER_APPROVE');

  const user = useSelector(state => state.user.user);
  const token = user?.token;
  const headBranchId = user?.headBranch?.headBranchId;
  const branchId = user?.branch?.branchId;

  const initialQuery = {
    page: 1,
    limit: 10,
    headBranchId,
    branchId,
    searchInput: '',
  };

  const [query, setQuery] = useState(initialQuery);
  const { control, getValues } = useForm({ defaultValues: initialQuery });

  const { data: list } = useQuery({
    queryKey: [config.queryKey, query],
    queryFn: () => config.fetchList(qs.stringify(query), token),
    enabled: !!token && !!branchId,
  });

  const approveMutation = useMutation({
    mutationFn: ({ id, receiverMemberId, rowConfig }) => rowConfig.approve(id, token, receiverMemberId),
    onSuccess: () => {
      toast.success(t('Pass transfer approved'));
      queryClient.invalidateQueries({ queryKey: [config.queryKey] });
      setReceiverPick(null);
    },
  });

  // 수신자 후보가 여러 명일 때 고르게 하는 상태. { id, candidates }
  const [receiverPick, setReceiverPick] = useState(null);

  // 양도 롤백(취소) 확인 대상 (id 보관). null이면 다이얼로그 닫힘.
  const [cancelTargetId, setCancelTargetId] = useState(null);

  const cancelMutation = useMutation({
    mutationFn: ({ id, rowConfig }) => rowConfig.cancel(id, token),
    onSuccess: () => {
      toast.success(t('Transfer canceled'));
      queryClient.invalidateQueries({ queryKey: [config.queryKey] });
      setCancelTargetId(null);
    },
    onError: () => {
      // 406(당일 아님/양수자 사용) 등 에러 토스트는 전역 mutationCache.onError가 처리하므로
      // 여기서는 확인 다이얼로그만 닫는다(중복 토스트 방지).
      setCancelTargetId(null);
    },
  });

  const applySearch = () => {
    setQuery(prev => ({ ...prev, page: 1, searchInput: getValues('searchInput') }));
  };

  // 같은 이름·전화번호 회원이 여러 명이면 엉뚱한 사람에게 넘어갈 수 있어 먼저 고르게 한다.
  const handleApprove = async (e, row) => {
    e.stopPropagation();
    const rowConfig = configOf(row);
    const id = row?.[config.idField];
    try {
      const candidates = await rowConfig.fetchReceivers(id, token);
      if (Array.isArray(candidates) && candidates.length > 1) {
        setReceiverPick({ id, candidates, rowConfig });
        return;
      }
    } catch {
      // 후보 조회가 실패해도 승인 자체를 막지는 않는다. 여러 명이면 백엔드가 다시 막는다.
    }
    approveMutation.mutate({ id, rowConfig });
  };

  const handleClickCancel = (e, row) => {
    e.stopPropagation();
    setCancelTargetId({ id: row?.[config.idField], rowConfig: configOf(row) });
  };

  const handleConfirmCancel = () => {
    if (cancelTargetId != null) {
      cancelMutation.mutate(cancelTargetId);
    }
  };

  const handleChangePage = (_, value) => {
    setQuery(prev => ({ ...prev, page: value }));
  };

  const rows = list?.content ?? [];

  return (
    <AnimationPage>
      <Card elevation={2}>
        <SearchRow>
          <SearchGroup>
            <SearchLabel className="title">{t('Search')}</SearchLabel>
            <FormInputText
              color="info"
              sx={{ width: '375px' }}
              size="small"
              placeholder="내용을 입력해주세요"
              id="outlined-search"
              type="search"
              name="searchInput"
              control={control}
              onKeyDown={event => {
                if (event.key === 'Enter') applySearch();
              }}
            />
            <SearchButtonWrapper>
              <CommonSearchSubmitButton onClick={applySearch}>{t('Search')}</CommonSearchSubmitButton>
            </SearchButtonWrapper>
          </SearchGroup>
        </SearchRow>

        <Divider sx={{ mb: '20px', mt: '26px', color: '#e1e1e1' }} />

        <StyledTableContainer>
          <StyledTable size="small" aria-label="transfer table">
            <StyledTableHead>
              <BodyRow>
                <HeaderCell align="center" sx={{ width: '200px' }}>
                  {t(config.headerLabelKey)}
                </HeaderCell>
                <HeaderCell align="center">{t('Name of recipient')}</HeaderCell>
                <HeaderCell align="center">{t('Mobile phone number of recipient')}</HeaderCell>
                <HeaderCell align="center">{t('Author')}</HeaderCell>
                <HeaderCell align="center">{t('Date')}</HeaderCell>
                <HeaderCell align="center">{t('Status')}</HeaderCell>
                <HeaderCell align="center">{t('Transfer Cancel')}</HeaderCell>
              </BodyRow>
            </StyledTableHead>

            <TableBody>
              {rows.map((row, index) => {
                const isPending = row?.status === STATUS_PENDING;
                const isApproved = row?.status === STATUS_APPROVED;
                const isCanceled = row?.status === STATUS_CANCELED;
                // 양도 취소(롤백) 가능 여부는 서버가 판단한다 — 승인·당일·양수자 권이 아직 ACTIVE(같은 날 재양도·만료면 불가).
                const canRollback = isApproved && row?.cancelable === true;
                return (
                  <BodyRow key={index}>
                    <BodyCell align="center">
                      <NameCellInner>
                        <NameText isPending={isPending}>
                          {type === 'ALL' ? `[${t(TYPE_LABEL[row?.type] ?? '-')}] ` : ''}
                          {row?.[config.nameField]}
                        </NameText>
                        {isPending && <PendingDot />}
                      </NameCellInner>
                    </BodyCell>
                    <BodyCell align="center">{row?.receiverName}</BodyCell>
                    <BodyCell align="center">{row?.receiverPhoneNumber}</BodyCell>
                    <BodyCell align="center">{row?.senderName}</BodyCell>
                    <BodyCell align="center">{row?.transferredDate}</BodyCell>
                    <BodyCell align="center">
                      <ApproveButton
                        isPending={isPending}
                        disabled={!isPending || approveMutation.isPending || !canApprove}
                        onClick={e => handleApprove(e, row)}>
                        {isPending ? t('Transfer Approve') : isCanceled ? t('Transfer Canceled') : t('Transfer Approved')}
                      </ApproveButton>
                    </BodyCell>
                    <BodyCell align="center">
                      {canRollback && (
                        <CancelButton
                          disabled={cancelMutation.isPending}
                          onClick={e => handleClickCancel(e, row)}>
                          {t('Transfer Cancel')}
                        </CancelButton>
                      )}
                    </BodyCell>
                  </BodyRow>
                );
              })}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>

        <CommonPagination
          totalElements={list?.totalElements}
          limit={query.limit}
          page={query.page}
          onChange={handleChangePage}
        />
      </Card>

      <Dialog open={cancelTargetId != null} onClose={() => setCancelTargetId(null)}>
        <DialogTitle>{t('Transfer Cancel')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('Are you sure you want to cancel this transfer?')}</DialogContentText>
          {/* 라커는 같이 넘긴 묶음을 통째로 되돌린다(#166) */}
          {cancelTargetId?.rowConfig === TRANSFER_CONFIG.LOCKER && (
            <DialogContentText sx={{ mt: 1, fontSize: 13 }}>
              {t('같은 자리에서 함께 넘긴 라커 이용권이 있으면 모두 함께 취소됩니다.')}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTargetId(null)}>{t('Cancel')}</Button>
          <Button disabled={cancelMutation.isPending} onClick={handleConfirmCancel} autoFocus>
            {t('Confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 같은 이름·전화번호 회원이 여러 명일 때 수신자 선택 */}
      <Dialog open={receiverPick != null} onClose={() => setReceiverPick(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t('수신자 선택')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            {t('같은 이름·전화번호 회원이 여러 명입니다. 수강권을 받을 회원을 선택해주세요.')}
          </DialogContentText>
          <List dense>
            {(receiverPick?.candidates || []).map(candidate => (
              <ListItemButton
                key={candidate.memberId}
                disabled={approveMutation.isPending}
                onClick={() =>
                  approveMutation.mutate({
                    id: receiverPick.id,
                    receiverMemberId: candidate.memberId,
                    rowConfig: receiverPick.rowConfig,
                  })
                }>
                <ListItemText
                  primary={`${candidate.name} (${candidate.phoneNumber || '-'})`}
                  secondary={`${t('등록일')} ${candidate.registeredDate || '-'}`}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiverPick(null)}>{t('Cancel')}</Button>
        </DialogActions>
      </Dialog>
    </AnimationPage>
  );
};

export default TransferTable;
